import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Contract attention — the rule-derived "what needs attention" list for
 * Contract Compliance (apps/api services/contract-attention.ts). Computed
 * on read from live contract state; no AI. The query key carries the
 * engagement id, so the engagement live-refresh sweep keeps it current.
 */

export const AttentionItemSchema = z.object({
  kind: z.string(),
  severity: z.enum(['critical', 'high', 'medium', 'info']),
  title: z.string(),
  detail: z.string(),
  count: z.number(),
  target: z.enum(['deliverables', 'deviations', 'changes', 'checkpoints', 'pulse', 'proposals']),
  action_label: z.string(),
});
export type AttentionItem = z.infer<typeof AttentionItemSchema>;

export const ContractAttentionSchema = z.object({
  generated_at: z.string(),
  items: z.array(AttentionItemSchema),
  divergent_items: z.array(
    z.object({
      register_entry_id: z.string(),
      title: z.string(),
      vendor_claim: z.string(),
      avero_verification: z.string(),
    }),
  ),
});
export type ContractAttention = z.infer<typeof ContractAttentionSchema>;

export function useContractAttentionQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['contract-attention', engagementId] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/contract/engagements/${encodeURIComponent(engagementId!)}/attention`,
        ContractAttentionSchema,
      ),
    enabled: Boolean(engagementId),
  });
}
